'use client';

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { ButtonBar } from '#/components/ButtonBar/ButtonBar';
import { Divider } from '#/components/Divider/Divider';
import { Form } from '#/components/Form/Form';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import { ImagePickerField } from '#/components/Form/ImagePickerField';
import { Hide } from '#/components/Hide/Hide';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { OrgStatBadge } from '#/components/OrgStatBadge/OrgStatBadge';
import { PageToolbar } from '#/components/PageToolbar/PageToolbar';
import { PrintHidden } from '#/components/PrintHidden/PrintHidden';
import { SocialLinks } from '#/components/SocialLinks/SocialLinks';
import { BodySmall } from '#/components/Text/BodySmall';
import { H1 } from '#/components/Text/H1';
import { useAppConfig } from '#/config/AppConfigContext';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingTypeLabel } from '#/modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import { ListingViewBody } from '#/modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewMain } from '#/modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSidebar } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSignupCta } from '#/modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { useSaveListing } from '#/modules/listing/hooks/useSaveListing';
import type { ApiOrg } from '#/modules/listing/orgs/api/types/ApiOrg';
import { useOrgSavedToast } from '#/modules/listing/orgs/hooks/useOrgSavedToast';
import { viewOrgReferencePath } from '#/modules/listing/routing/listing.paths';
import { orgDashboardApiPartialUpdateOrg } from '#/modules/orgDashboard/api/orgDashboardApiOrg';
import type { OrgDashboardApiOrg } from '#/modules/orgDashboard/api/types/OrgDashboardApiOrg';
import {
  orgDashboardCreateJobPath,
  orgDashboardEditOrgPath,
} from '#/modules/orgDashboard/routing/orgDashboard.paths';
import { SearchContextHeader } from '#/modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { useRouteSearchParams } from '#/routing/hooks/useRouteSearchParams';
import type { DatetimeString } from '#/types/TypedDateString';
import { getCityStateString } from '#/utils/address/getCityStateString';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { trackPageView } from '#/utils/analytics/track/trackPageView';
import { splitSocialAndNonSocialLinks } from '#/utils/social/getSocialNetworkFromLink';
import { formatToHumanReadableUrl } from '#/utils/string/formatToHumanReadableLink/formatToHumanReadableUrl';
import { showModal } from '#/zustand-stores/modalStore';

import { Banner } from './components/Banner';
import { OrgPreviewInfo } from './components/OrgPreviewInfo';
import {
  ViewOrgPageFooterHolder,
  ViewOrgPageFooterWrapper,
  ViewOrgPageHeaderSocialLinks,
  ViewOrgPageLayoutHeader,
  ViewOrgPageLayoutSidebarTitle,
} from './ViewOrg.styled';
import { ViewOrgPageActionButtons } from './ViewOrgPageActionButtons';
import { ViewOrgPageDetails } from './ViewOrgPageDetails';
import { ViewOrgPageSidebarDetails } from './ViewOrgPageSidebarDetails';
import { ViewOrgPageSidebarHeader } from './ViewOrgPageSidebarHeader';
import { ViewOrgPageSidebarImage } from './ViewOrgPageSidebarImage';
import { ViewOrgPageSidebarTitle } from './ViewOrgPageSidebarTitle';
import { isViewOrgPageListingTabSlug } from './ViewOrgPageTabSlug';

type Props = {
  initialOrg: ApiOrg;
  initiallySaved: boolean;
  onUpdateOrg?: (org: OrgDashboardApiOrg) => void;
  previewOrg: OrgDashboardApiOrg | null;
  now: DatetimeString;
};

export function ViewOrgPage({
  initialOrg,
  initiallySaved,
  onUpdateOrg,
  previewOrg,
  now,
}: Props) {
  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();
  const isPreview = Boolean(previewOrg);

  const [org, setOrg] = useState<ApiOrg>(initialOrg);

  const location = useLocation();

  const searchParams = useRouteSearchParams(viewOrgReferencePath);
  const tab = isViewOrgPageListingTabSlug(searchParams.tab)
    ? searchParams.tab
    : 'JOB';

  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(true);
  const { user } = useUser();
  const { socialLinks, nonSocialLinks } = splitSocialAndNonSocialLinks(
    org.links,
  );

  const { searchContext } = useSearchContextHeader({
    currentObjectId: org.id,
  });

  const isOwner = useMemo(
    () => Boolean(user?.orgs?.some((o) => o.id === org.id)),
    [user, org],
  );

  useEffect(() => {
    if (isPreview) return;

    trackPageView('Org Listing Page', {
      name: org.name,
      id: org.id,
      org_type: org.orgType,
      page_type: 'Org Listings',
    });
  }, [org.name, org.id, org.orgType, isPreview]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    if (
      queryParams.has('admin-request') &&
      user?.emailVerified &&
      user?.firstName
    ) {
      showModal('ADMIN_REQUEST', { objectId: org.id });
      queryParams.delete('admin-request');

      const newUrl = `${location.pathname}?${queryParams}`;
      void navigate(newUrl);
    }
  }, [
    location.pathname,
    location.search,
    navigate,
    org.id,
    org.name,
    user?.emailVerified,
    user?.firstName,
  ]);

  const onSubmit: FormSubmitFunction<
    Pick<ApiOrg, 'id'> & Partial<ApiOrg>
  > = async ({ values, handleError }) => {
    try {
      const apiOrgResponse = await orgDashboardApiPartialUpdateOrg(values);
      setOrg(apiOrgResponse);
      onUpdateOrg?.(apiOrgResponse);
    } catch (error) {
      handleError(error as Error);
    }
  };

  const orgStat = useMemo(() => {
    let fragment;
    if (org.hiring) {
      fragment = (
        <OrgStatBadge iconName="star-check" text={i18n('Actively Hiring')} />
      );
    } else if (org.hasUpcomingEvents) {
      fragment = (
        <OrgStatBadge iconName="calendar" text={i18n('Upcoming Events')} />
      );
    } else if (org.seekingVolunteers) {
      fragment = (
        <OrgStatBadge iconName="group" text={i18n('Seeking Volunteers')} />
      );
    }
    if (fragment) {
      return <div style={{ marginBottom: 24 }}>{fragment}</div>;
    }
    return null;
  }, [i18n, org.hasUpcomingEvents, org.hiring, org.seekingVolunteers]);

  const { showOrgSavedToast } = useOrgSavedToast();

  const { save, saved, unsave } = useSaveListing({
    listingId: org.id,
    listingType: 'ORG',
    initiallySaved,
    orgId: org.id,
    orgType: org.orgType,
    onSaved: showOrgSavedToast,
    onUnsaved: null,
    trackClickEvent: true,
    searchSavedListingsStore: null,
  });

  return (
    <>
      {isOwner && (
        <PageToolbar
          centered
          message={
            org.isFrozen
              ? i18n(
                  'Your organization has been frozen. Please contact the Idealist Community Support Team to resolve this issue.',
                )
              : i18n('You are an administrator of this organization')
          }
          tools={
            <>
              {org.isFrozen && (
                <Button
                  type="link"
                  href="https://www.idealist.org/en/about/contact"
                  size="small"
                  variant="secondary-white"
                >
                  {i18n('Contact')}
                </Button>
              )}

              <Button
                type="link"
                href={orgDashboardEditOrgPath.with({ orgId: org.id })}
                qaId="edit-button"
                size="small"
                variant="secondary-white"
                onClick={() => {
                  if (!org.isFrozen && isPreview) {
                    trackClicked('Org Preview - Edit Org Profile');
                  }
                }}
              >
                {i18n('Edit')}
              </Button>

              {editMode && (
                <Button
                  type="button"
                  size="small"
                  onClick={() => {
                    trackClicked('Org Profile: Admin Preview Mode');
                    setEditMode(false);
                  }}
                  qaId="preview-button"
                  variant="secondary-white"
                >
                  {isPreview ? (
                    i18n('Preview')
                  ) : (
                    <>
                      <Hide sm md lg xl>
                        {i18n('Preview')}
                      </Hide>
                      <Hide xs>{i18n('View Public Profile')}</Hide>
                    </>
                  )}
                </Button>
              )}

              {!editMode && (
                <Button
                  type="button"
                  size="small"
                  onClick={() => {
                    trackClicked(`Org Profile: Admin Edit Mode`);
                    setEditMode(true);
                  }}
                  qaId="preview-button"
                  variant="secondary-white"
                >
                  {i18n('View Edit Mode')}
                </Button>
              )}

              {!org.isFrozen &&
                (('is501c3' in org && org.is501c3) || !isPreview) && (
                  <Button
                    type="link"
                    size="small"
                    variant="secondary-white"
                    href={orgDashboardCreateJobPath.with({ orgId: org.id })}
                    onClick={() => {
                      if (isPreview) {
                        trackClicked('Org Preview - Post a Job');
                      } else {
                        trackClicked('Org Profile - Post a Job');
                      }
                    }}
                  >
                    {i18n('Post a Job')}
                  </Button>
                )}
            </>
          }
        />
      )}

      {previewOrg && !org.isFrozen && (
        <OrgPreviewInfo org={previewOrg} now={now} />
      )}

      {((isOwner && editMode) || org.banner) && (
        <Box height={[isOwner ? 80 : 35, null, null, 200]}>
          {isOwner && editMode ? (
            <Form
              analyticsTitle="[main] Org page - banner"
              usageContext="page"
              initialValues={{
                banner: org.banner,
                id: org.id,
              }}
              onSubmit={onSubmit}
              render={({ submitForm }) => (
                <ImagePickerField
                  name="banner"
                  format="banner"
                  message={i18n('Drag a banner image here')}
                  aspectRatio={7.2}
                  altText={org.name}
                  overlayButton
                  onChange={(image) => {
                    trackClicked(`Org profile: upload banner`);
                    void submitForm({ banner: image });
                  }}
                  confirmModal
                  extraAnalyticsData={{
                    context: 'Org Page',
                    file_type: 'Org Banner',
                    file_label: 'Drag a banner image here',
                  }}
                  showCropScreen
                />
              )}
            />
          ) : (
            org.banner && (
              <Banner
                $handle={org.banner.handle}
                $mimetype={org.banner.mimetype}
                $appConfig={appConfig}
                data-qa-id="banner-image"
              />
            )
          )}
        </Box>
      )}

      <ListingViewBody>
        <ListingViewMain>
          <ViewOrgPageLayoutHeader>
            {searchContext && (
              <PrintHidden>
                <Box mb={[4, 4, 4, '40px']}>
                  <SearchContextHeader searchContext={searchContext} />
                </Box>
              </PrintHidden>
            )}

            <Box mb="24px">
              <ListingTypeLabel listingType="ORG" orgType={org.orgType} />
            </Box>

            <H1
              styleAs="h2"
              color="mediumContentGrey"
              qaId="listing-name"
              lineHeight={1}
              mb="24px"
            >
              {org.name}
            </H1>
            <Box
              lineHeight={1}
              mb={['12px', null, null, '24px']}
              display="flex"
              alignItems="center"
              gridGap={16}
              flexWrap="wrap"
            >
              {org.address && (
                <div>{getCityStateString(i18n, org.address)}</div>
              )}

              {nonSocialLinks.map((link) => (
                <Fragment key={link}>
                  <div>{' | '}</div>
                  <StyledNativeLinkWithRef
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {formatToHumanReadableUrl(link)}
                  </StyledNativeLinkWithRef>
                </Fragment>
              ))}
            </Box>

            <ViewOrgPageHeaderSocialLinks>
              <SocialLinks
                socialLinks={socialLinks}
                qaId="org-social-links-header"
              />
            </ViewOrgPageHeaderSocialLinks>

            <Hide xs sm md>
              <div>
                <ViewOrgPageActionButtons
                  org={org}
                  isPreview={isPreview}
                  mobile={false}
                  saved={saved}
                  onSave={save}
                  onUnsave={unsave}
                />
              </div>
            </Hide>
          </ViewOrgPageLayoutHeader>

          <ViewOrgPageDetails org={org} tab={tab} now={now} />

          {searchContext && (
            <PrintHidden>
              <Box mt={70} mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </ListingViewMain>

        <ListingViewSidebar qaId="org-page-sidebar" maxWidth={288}>
          <BodySmall lineHeight="normal">
            <Hide xs sm md>
              <Box mb="16px">
                <ViewOrgPageSidebarImage
                  org={org}
                  orgOwner={isOwner}
                  editMode={editMode}
                  onSubmit={onSubmit}
                />
              </Box>
            </Hide>

            {orgStat}

            <ViewOrgPageLayoutSidebarTitle aria-hidden="true">
              <Hide xs sm md>
                <ViewOrgPageSidebarTitle org={org} />
              </Hide>
            </ViewOrgPageLayoutSidebarTitle>

            <SocialLinks
              socialLinks={socialLinks}
              qaId="org-social-links-sidebar"
            />

            <Divider marginTop={24} marginBottom={24} />

            <ViewOrgPageSidebarHeader org={org} />

            <ViewOrgPageSidebarDetails org={org} orgOwner={isOwner} />
          </BodySmall>
        </ListingViewSidebar>
      </ListingViewBody>

      {!isPreview && !user && (
        <PrintHidden>
          <ViewOrgPageFooterHolder>
            <ViewOrgPageFooterWrapper>
              <ListingViewSignupCta listingUrl={org.url[locale]} type="ORG" />
            </ViewOrgPageFooterWrapper>
          </ViewOrgPageFooterHolder>
        </PrintHidden>
      )}

      <ButtonBar>
        <ViewOrgPageActionButtons
          org={org}
          isPreview={isPreview}
          mobile
          saved={saved}
          onSave={save}
          onUnsave={unsave}
        />
      </ButtonBar>
    </>
  );
}
